/**
 * Vista: Impresión de Orden de Trabajo
 * Formato: AMX-PR-FR-01_R12
 * Template HTML optimizado para impresión (2 páginas)
 */

import produccionService from '../services/produccion.service.js';

export default {
  async render() {
    return `
      <div class="loading-container" style="text-align: center; padding: 3rem;">
        <div class="spinner"></div>
        <p>Cargando formato para impresión...</p>
      </div>
    `;
  },

  async afterRender() {
    const path = window.location.pathname;
    const id = path.split('/')[3]; // /produccion/ordenes-trabajo/:id/print

    try {
      // Fetch orden y plantilla completa en paralelo
      const [ordenResponse, plantillaResponse] = await Promise.all([
        produccionService.obtenerOrdenTrabajo(id),
        produccionService.obtenerPlantillaCompleta().catch(() => ({ data: [] }))
      ]);
      const orden = ordenResponse.data || ordenResponse;
      const plantilla = plantillaResponse.data || plantillaResponse || [];
      this.renderPrint(orden, plantilla);

      // Ajustar escala para pantallas pequeñas
      this.fitToViewport();

      // Auto-abrir diálogo de impresión después de cargar
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error) {
      console.error('Error:', error);
      document.getElementById('content').innerHTML = `
        <div style="padding: 2rem; text-align: center;">
          <h2>Error al cargar orden de trabajo</h2>
          <p>No se pudo cargar la información para imprimir.</p>
          <button onclick="window.history.back()" class="btn btn-secondary">Volver</button>
        </div>
      `;
    }
  },

  // Mapeo nivel_visual → segmento (1-7)
  NIVEL_A_SEGMENTO: {
    'min': 1,
    'bajo': 2,
    'medio_bajo': 3,
    'medio': 4,
    'medio_alto': 5,
    'alto': 6,
    'max': 7
  },

  // Colores por segmento para cada fluido (igual que en la vista)
  // Solo los segmentos con color definido se pintan, los demás son blancos
  FLUIDOS_COLORES: {
    combustible:               { 1:'#dc3545', 4:'#ffc107', 7:'#28a745' },
    anticongelante:            { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_sistema_hidraulico: { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_motor:              { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_transmision:        { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_sistema_direccion:  { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_frenos:             { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_eje_delantero:      { 1:'#dc3545', 4:'#28a745', 5:'#28a745', 7:'#dc3545' },
    aceite_maza_izquierda:     { 1:'#dc3545', 4:'#28a745', 7:'#dc3545' },
    aceite_maza_derecha:       { 1:'#dc3545', 4:'#28a745', 7:'#dc3545' }
  },

  // Renderizar barra de nivel de fluido (igual estructura que la vista)
  // noAplica=true → el nivel no aplica para el modelo (config dinámica en `modelos`).
  renderNivelBar(fluidoKey, label, nivelVisual, noAplica = false) {
    const segmentoActivo = this.NIVEL_A_SEGMENTO[nivelVisual] || 0;
    const colores = this.FLUIDOS_COLORES[fluidoKey] || {};

    let segments = '';
    for (let i = 1; i <= 7; i++) {
      const isActive = segmentoActivo === i;
      const bgColor = colores[i] || '#fff';
      const mark = isActive ? 'X' : '';

      segments += `<td class="nivel-seg" style="background:${bgColor};">${mark}</td>`;
    }

    // Marca de agua diagonal "NO APLICA" SOBRE la barra (conserva su diseño/colores).
    const watermark = noAplica ? `
      <div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;pointer-events:none;">
        <span style="transform:rotate(-14deg);font-size:8px;font-weight:bold;color:rgba(220,53,69,0.85);background:rgba(255,255,255,0.6);border:1px solid rgba(220,53,69,0.85);padding:0.5px 5px;border-radius:3px;letter-spacing:0.5px;white-space:nowrap;">NO APLICA</span>
      </div>` : '';

    return `
      <div class="nivel-item" style="position:relative;">
        <div class="nivel-label">${label}</div>
        <table class="nivel-table">
          <tr>${segments}</tr>
          <tr>
            <td class="nivel-minmax-cell">Min</td>
            <td colspan="5"></td>
            <td class="nivel-minmax-cell" style="text-align:right;">Max</td>
          </tr>
        </table>
        ${watermark}
      </div>
    `;
  },

  // Nombres cortos para headers de columnas de modelo
  MODEL_SHORT_NAMES: {
    'TE254 CLASSIC': 'TE254',
    'TE354 III GEN': 'TE354',
    'TB504 III GEN': 'TB504',
    'TH754 III GEN': 'TH754',
    'TD904 III GEN': 'TD904',
    'TD1104 III GEN': 'TD1104',
    'TR1504 III GEN': 'TR1504'
  },

  ALL_MODELS: [
    'TE254 CLASSIC', 'TE354 III GEN', 'TB504 III GEN', 'TH754 III GEN',
    'TD904 III GEN', 'TD1104 III GEN', 'TR1504 III GEN'
  ],

  MODEL_COLORS: {
    'TE254 CLASSIC': '#98BCDE',
    'TE354 III GEN': '#E6E4E3',
    'TB504 III GEN': '#E47B35',
    'TH754 III GEN': '#EFE940',
    'TD904 III GEN': '#6EA647',
    'TD1104 III GEN': '#F5D269',
    'TR1504 III GEN': '#4564AE'
  },

  /**
   * Genera HTML de una tabla de mantenimiento para un intervalo de horas dado
   */
  renderMantenimientoTable(intervaloHoras, items, modeloActual, itemsRealizados, showHeader = true) {
    const modelShort = this.MODEL_SHORT_NAMES;
    const models = this.ALL_MODELS;
    const modeloNorm = (modeloActual || '').toUpperCase().trim();
    const modeloIdx = models.findIndex(m => m === modeloActual || modeloNorm.includes(m.split(' ')[0]) || m.toUpperCase().includes(modeloNorm));

    const headerModels = models.map((m, i) => {
      const isActive = i === modeloIdx;
      const cls = 'mant-model-header';
      const parts = m.match(/^(\S+)\s*(.*)$/);
      const displayName = parts ? `${parts[1]}${parts[2] ? '<br>' + parts[2] : ''}` : m;
      return `<th class="${cls}"><div class="mant-model-rotated">${displayName}</div></th>`;
    }).join('');

    const totalRows = items.length;

    const rows = items.map((item, idx) => {
      const realizadoKey = `${item.item_numero}_${item.intervalo_horas}`;
      const esRealizado = itemsRealizados.has(realizadoKey);

      const modelCells = models.map((m, i) => {
        const symbol = item.modelos[m] || '';
        const isActive = i === modeloIdx;
        let cls = 'mant-model-cell';
        let style = '';
        if (symbol) {
          const color = this.MODEL_COLORS[m] || '#cde4f0';
          cls += ' mant-cell-symbol';
          style = ` style="background:${color} !important;"`;
        } else {
          style = ` style="background:#000 !important;"`;
        }
        let displaySymbol = symbol === 'X' ? '' : symbol;
        if (isActive && esRealizado) displaySymbol = 'X';
        return `<td class="${cls}"${style}>${displaySymbol}</td>`;
      }).join('');

      const intervaloCell = idx === 0
        ? `<td class="mant-interval-cell" rowspan="${totalRows}"><div class="mant-interval-rotated">${intervaloHoras}HR</div></td>`
        : '';

      return `
        <tr>
          ${intervaloCell}
          <td class="mant-num">${item.item_numero}</td>
          <td class="mant-desc">${item.descripcion}</td>
          <td class="mant-acc">${item.accion}</td>
          <td class="mant-pts">${item.puntos}</td>
          ${modelCells}
        </tr>
      `;
    }).join('');

    const thead = showHeader ? `
        <thead>
          <tr>
            <th class="mant-th-interval"></th>
            <th class="mant-th-num"></th>
            <th class="mant-th-desc"><div class="mant-header-label">PARTE DE MANTENIMIENTO</div></th>
            <th class="mant-th-acc"><div class="mant-header-label">ACCIÓN</div></th>
            <th class="mant-th-pts"><div class="mant-model-rotated">PUNTOS</div></th>
            ${headerModels}
          </tr>
        </thead>` : '';

    const colgroup = `
        <colgroup>
          <col style="width:5mm;">
          <col style="width:5%;">
          <col style="width:30%;">
          <col style="width:12%;">
          <col style="width:4%;">
          ${models.map(() => '<col style="width:6.4%;">').join('')}
        </colgroup>`;

    return `
      <table class="mant-full-table">
        ${colgroup}
        ${thead}
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;
  },

  /**
   * Genera el HTML completo de la sección de mantenimiento con layout de 2 columnas
   */
  renderMantenimientoCompleto(plantilla, modeloActual, itemsOrden) {
    // Crear set de items realizados: key = "item_numero_intervalo_horas"
    const itemsRealizados = new Set();
    (itemsOrden || []).forEach(item => {
      if (item.realizado) {
        itemsRealizados.add(`${item.item_numero}_${item.intervalo_horas}`);
      }
    });

    // Agrupar plantilla por intervalo
    const porIntervalo = {};
    (plantilla || []).forEach(item => {
      if (!porIntervalo[item.intervalo_horas]) {
        porIntervalo[item.intervalo_horas] = [];
      }
      porIntervalo[item.intervalo_horas].push(item);
    });

    const intervalos = [50, 350, 650, 950, 1250, 1550, 1850];

    // Columna izquierda: 50HR
    const tabla50 = porIntervalo[50]
      ? this.renderMantenimientoTable(50, porIntervalo[50], modeloActual, itemsRealizados)
      : '';

    // Columna derecha: 350HR - 1850HR apilados (solo header en la primera)
    const tablasDerechas = intervalos.slice(1).map((intervalo, idx) => {
      const items = porIntervalo[intervalo];
      if (!items || items.length === 0) return '';
      return this.renderMantenimientoTable(intervalo, items, modeloActual, itemsRealizados, idx === 0);
    }).join('');

    // Simbología (formato original: texto corrido)
    const simbologia = `
      <div class="mant-simbologia">
        <div>Simbología:</div>
        <div>S/Filtro de succión, R/Filtro de retorno, C/Tractor cabinado, H/Tractor alto despeje, 4 o 2/Puntos de inspección</div>
      </div>
    `;

    // Reporte CRM
    const reporteCRM = `
      <div class="mant-reporte-crm"><b style="color:red;">REPORTE CRM:</b> <span style="flex:1; border-bottom:1px solid #000;"></span></div>
    `;

    return `
      <div class="mant-layout-2col">
        <div class="mant-col-left">
          ${tabla50}
        </div>
        <div class="mant-col-right">
          ${tablasDerechas}
          ${simbologia}
          ${reporteCRM}
        </div>
      </div>
    `;
  },

  fitToViewport() {
    const container = document.querySelector('.print-ot-container');
    if (!container) return;
    const pageWidth = 816; // 216mm ≈ 816px
    const vw = document.documentElement.clientWidth;
    if (vw < pageWidth) {
      container.style.zoom = vw / pageWidth;
    }
  },

  renderPrint(o, plantilla = []) {
    const html = this.generateHTML(o, plantilla);
    document.getElementById('content').innerHTML = html;
  },

  generateHTML(o, plantilla = []) {
    const fp = (o.fecha || '').substring(0, 10).split('-');
    const fecha = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : '-';
    const modelo = o.tractor_modelo || o.modelo || '';

    // Obtener niveles previos y posteriores
    const nivelesPrevios = o.niveles_fluidos?.previos || o.niveles?.filter(n => n.momento === 'previo') || [];
    const nivelesPosteriores = o.niveles_fluidos?.posteriores || o.niveles?.filter(n => n.momento === 'posterior') || [];

    // Config dinámica: niveles que no aplican para el modelo (resuelta por el backend,
    // columna `modelos.niveles_no_aplican` compartida con el Check List de Ensamble).
    const nivelesNA = Array.isArray(o.niveles_no_aplican) ? o.niveles_no_aplican : [];
    const na = (key) => nivelesNA.includes(key);

    const getNivel = (niveles, tipo) => {
      const n = niveles.find(x => x.tipo_fluido === tipo);
      return n ? n.nivel_visual : null;
    };

    // Marcas visuales de inspección
    const marcas = Array.isArray(o.marcas_visuales) ? o.marcas_visuales : [];
    // Tamaño por marca: el diámetro impreso = 2 × radius_pct (% del ancho del
    // contenedor). Marcas viejas sin radius_pct → fallback al tamaño anterior (5%).
    // IMPORTANTE: solo seteamos `width`. La altura se deriva via `aspect-ratio: 1`
    // del CSS (.ot-mark) para garantizar círculo perfecto. Si se ponía
    // `height: X%`, el % se calculaba sobre la ALTURA del contenedor (no el
    // ancho) → el círculo quedaba ovalado en imágenes landscape.
    const markStyle = (m) => {
      const rPct = (typeof m.radius_pct === 'number' && m.radius_pct > 0) ? m.radius_pct : 2.5;
      const diamPct = Math.min(50, Math.max(2, rPct * 2));
      return `left:${m.x}%; top:${m.y}%; width:${diamPct}%;`;
    };

    // Items de mantenimiento
    const items = o.items_mantenimiento || [];

    // Diagnósticos
    const diagnosticos = o.diagnosticos || [];

    const html = `
      <div class="print-ot-container">
        <!-- ==================== PÁGINA 1 ==================== -->
        <div class="print-page page-1">
          <!-- Header -->
          <div class="print-header">
            <img src="/img/Logo _LOVOL.png" alt="LOVOL" class="logo-left" onerror="this.style.display='none'" />
            <h1 class="print-title">ORDEN DE TRABAJO TRACTOR</h1>
            <img src="/img/Logo_AGRO.png" alt="AGROMX" class="logo-right" onerror="this.style.display='none'" />
          </div>
          <div style="border-bottom: 3px solid #2352A4; margin-bottom: 4mm;"></div>

          <!-- Datos principales -->
          <div class="datos-grid">
            <div class="dato-row">
              <div class="dato-field dato-col-izq"><span class="dato-label">Número de serie:</span><span class="dato-value">${o.num_serie}</span></div>
              <div class="dato-field dato-col-der"><span class="dato-label dato-label-der">Fecha:</span><span class="dato-value">${fecha}</span><span class="dato-label" style="margin-left:4mm;">Horas motor:</span><span class="dato-value">${o.horas_motor || ''}</span></div>
            </div>
            <div class="dato-row">
              <div class="dato-field dato-col-izq"><span class="dato-label">Modelo:</span><span class="dato-value">${modelo}</span></div>
              <div class="dato-field dato-col-der"><span class="dato-label dato-label-der">Dirección:</span><span class="dato-value">${o.direccion || ''}</span></div>
            </div>
            <div class="dato-row">
              <div class="dato-field dato-col-izq"><span class="dato-label">Nombre del usuario:</span><span class="dato-value">${o.nombre_usuario || ''}</span></div>
              <div class="dato-field dato-col-der"><span class="dato-label dato-label-der">Distribuidor:</span><span class="dato-value">${o.distribuidor_nombre || ''}</span></div>
            </div>
          </div>

          <!-- Sección 1: Recepción de Equipo -->
          <div style="border-bottom: 3px solid #2352A4; margin-bottom: 2mm;"></div>
          <div class="section-title" style="margin-bottom:0; padding-bottom:0;">1.- RECEPCIÓN DE EQUIPO</div>
          <p class="section-subtitle">Revisión general del funcionamiento del tractor LOVOL</p>

          <div class="niveles-header">Revisión de los niveles de aditivos y lubricantes del tractor LOVOL (Previo al mantenimiento o reparación)</div>

          <div class="niveles-grid">
            ${this.renderNivelBar('combustible', 'NIVEL DE COMBUSTIBLE', getNivel(nivelesPrevios, 'combustible'), na('combustible'))}
            ${this.renderNivelBar('anticongelante', 'NIVEL DE ANTICONGELANTE', getNivel(nivelesPrevios, 'anticongelante'), na('anticongelante'))}
            ${this.renderNivelBar('aceite_sistema_direccion', 'NIVEL DE ACEITE DE SISTEMA DE DIRECCIÓN', getNivel(nivelesPrevios, 'aceite_sistema_direccion'), na('aceite_sistema_direccion'))}
            ${this.renderNivelBar('aceite_motor', 'NIVEL DE ACEITE DE MOTOR', getNivel(nivelesPrevios, 'aceite_motor'), na('aceite_motor'))}
            ${this.renderNivelBar('aceite_transmision', 'NIVEL DE ACEITE DE TRANSMISIÓN', getNivel(nivelesPrevios, 'aceite_transmision'), na('aceite_transmision'))}
            ${this.renderNivelBar('aceite_sistema_hidraulico', 'NIVEL DE ACEITE DE SISTEMA HIDRÁULICO', getNivel(nivelesPrevios, 'aceite_sistema_hidraulico'), na('aceite_sistema_hidraulico'))}
            ${this.renderNivelBar('aceite_frenos', 'NIVEL DE ACEITE DE FRENOS', getNivel(nivelesPrevios, 'aceite_frenos'), na('aceite_frenos'))}
            ${this.renderNivelBar('aceite_eje_delantero', 'NIVEL DE ACEITE DE EJE DELANTERO', getNivel(nivelesPrevios, 'aceite_eje_delantero'), na('aceite_eje_delantero'))}
            ${this.renderNivelBar('aceite_maza_izquierda', 'NIVEL DE ACEITE DE MAZA IZQUIERDA', getNivel(nivelesPrevios, 'aceite_maza_izquierda'), na('aceite_maza_izquierda'))}
            ${this.renderNivelBar('aceite_maza_derecha', 'NIVEL DE ACEITE MAZA DERECHA', getNivel(nivelesPrevios, 'aceite_maza_derecha'), na('aceite_maza_derecha'))}
          </div>

          <!-- Inspección Visual con marcas de daño -->
          <p class="img-caption">Revisión visual del estado físico del tractor LOVOL</p>
          <div class="ot-visual-layout">
            <div class="ot-visual-main-imgs">
              <div class="ot-img-container">
                <img src="/img/tractor-lateral-der.jpeg" alt="Lateral Derecho" onerror="this.parentElement.style.display='none'" />
                ${marcas.filter(m => m.img === 'tractor-lateral-der').map(m => `<div class="ot-mark" style="${markStyle(m)}"></div>`).join('')}
              </div>
              <div class="ot-img-container">
                <img src="/img/tractor-lateral-izq.jpeg" alt="Lateral Izquierdo" onerror="this.parentElement.style.display='none'" />
                ${marcas.filter(m => m.img === 'tractor-lateral-izq').map(m => `<div class="ot-mark" style="${markStyle(m)}"></div>`).join('')}
              </div>
            </div>
            <div class="ot-visual-cabin-box">
              <div class="ot-visual-cabin-label">Para tractor encabinado</div>
              <div class="ot-img-container">
                <img src="/img/tractor-frente.jpeg" alt="Parte Frontal" onerror="this.parentElement.style.display='none'" />
                ${marcas.filter(m => m.img === 'tractor-frente').map(m => `<div class="ot-mark" style="${markStyle(m)}"></div>`).join('')}
              </div>
              <div class="ot-img-container">
                <img src="/img/tractor-trasera.jpeg" alt="Parte Trasera" onerror="this.parentElement.style.display='none'" />
                ${marcas.filter(m => m.img === 'tractor-trasera').map(m => `<div class="ot-mark" style="${markStyle(m)}"></div>`).join('')}
              </div>
            </div>
          </div>
          <p class="img-disclaimer">*El diagrama es ilustrativo, y las características del tractor pueden variar según modelo.</p>

          <!-- Observaciones -->
          <div class="observaciones-section">
            <div class="obs-first-line">
              <span class="obs-label">Observaciones:</span>
              <span class="obs-line">${o.observaciones_visual || ''}</span>
            </div>
            <div class="obs-line-full"></div>
            <div class="obs-line-full"></div>
            <div class="obs-line-full"></div>
            <div class="obs-line-full"></div>
            <div class="obs-line-full"></div>
            <div class="obs-separator"></div>
          </div>

          <!-- Propiedad perteneciente al usuario -->
          <div class="propiedad-section">
            <div class="propiedad-title">PROPIEDAD PERTENECIENTE AL USUARIO</div>
            <div class="propiedad-content">
              <p>1. AGROMX Y/O EL DISTRIBUIDOR SE COMPROMETE A MANIPULAR Y CONTROLAR ADECUADAMENTE EL EQUIPO DESDE SU RECOLECCIÓN HASTA LA ENTREGA.</p>
              <p>2. AGROMX, EL DISTRIBUIDOR Y/O EL USUARIO VERIFICARON E IDENTIFICARON LAS CONDICIONES ACTUALES DEL EQUIPO.</p>
              <p>3. AGROMX Y/O EL DISTRIBUIDOR ES RESPONSABLE DE PROTEGER Y SALVAGUARDAR EL EQUIPO PROPIEDAD DEL USUARIO.</p>
            </div>
          </div>

          <!-- Firmas página 1 -->
          <div style="display:flex; justify-content:space-between; margin-top:4mm; gap:5mm;">
            <div style="flex:1; max-width:45%; border:none; outline:none; background:none;">
              ${o.tecnico_servicio_firma_url ? `<img src="${o.tecnico_servicio_firma_url}" style="width:100%; max-height:15mm; object-fit:contain; display:block; margin:0 auto 1mm;" onerror="this.style.display='none'">` : ''}
              ${o.tecnico_servicio ? `<div style="font-size:7pt; text-align:center; margin-bottom:1mm;">${o.tecnico_servicio}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:1mm; text-align:center; font-weight:bold; color:#2352A4;">TÉCNICO DE SERVICIO</div>
              <div style="font-size:6pt; text-align:center; color:#000; font-style:italic;">Nombre y firma</div>
            </div>
            <div style="flex:1; max-width:45%; border:none; outline:none; background:none;">
              ${o.usuario_firma_url ? `<img src="${o.usuario_firma_url}" style="width:100%; max-height:15mm; object-fit:contain; display:block; margin:0 auto 1mm;" onerror="this.style.display='none'">` : ''}
              ${o.nombre_usuario ? `<div style="font-size:7pt; text-align:center; margin-bottom:1mm;">${o.nombre_usuario}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:1mm; text-align:center; font-weight:bold; color:#2352A4;">USUARIO</div>
              <div style="font-size:6pt; text-align:center; color:#000; font-style:italic;">Nombre y firma</div>
            </div>
          </div>
          <div style="border-bottom: 3px solid #2352A4; margin-top: 4mm;"></div>

          <div class="page-footer">AMX-PR-FR-01_R12</div>
        </div>

        <!-- ==================== PÁGINA 2: MANTENIMIENTO ==================== -->
        <div class="print-page page-2">
          <!-- Header -->
          <div class="print-header">
            <img src="/img/Logo _LOVOL.png" alt="LOVOL" class="logo-left" onerror="this.style.display='none'" />
            <h1 class="print-title">ORDEN DE TRABAJO TRACTOR</h1>
            <img src="/img/Logo_AGRO.png" alt="AGROMX" class="logo-right" onerror="this.style.display='none'" />
          </div>

          <!-- Sección 2: Servicio de Mantenimiento -->
          <div class="section-title mant-section-title" style="margin-top:3mm; margin-bottom:2mm; padding:0;">2.- SERVICIO DE MANTENIMIENTO OBLIGATORIO</div>

          ${this.renderMantenimientoCompleto(plantilla, modelo, items)}

          <!-- Sección 3: Diagnóstico de falla -->
          <div class="section-title diag-section-title" style="margin-top:-4mm; margin-bottom:0; padding-bottom:0;">3.- DIAGNÓSTICO DE LA FALLA Y SOLUCIÓN</div>

          <table class="diagnostico-table" style="margin-top:0;">
            <thead>
              <tr>
                <th style="width:5%;">N°</th>
                <th style="width:30%;">DIAGNÓSTICO DE FALLA</th>
                <th style="width:30%;">SOLUCIÓN DE FALLA</th>
                <th style="width:15%;">CÓDIGO DE PARTE</th>
                <th style="width:10%;">ACCIÓN*</th>
                <th style="width:10%;">CANT.</th>
              </tr>
            </thead>
            <tbody>
              ${[0,1,2,3,4].map(i => {
                const d = diagnosticos[i];
                return `
                  <tr>
                    <td class="center">${i + 1}</td>
                    <td>${d?.diagnostico || ''}</td>
                    <td>${d?.solucion || ''}</td>
                    <td class="center">${d?.codigo_parte || ''}</td>
                    <td class="center">${d?.accion || ''}</td>
                    <td class="center">${d?.cantidad || ''}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="diag-footer-row">
            <span class="table-note">*Mantenimiento, reemplazar, observación.</span>
            <div class="diag-footer-right">
              <span class="garantia-check"><span class="checkbox">${o.garantia ? '☑' : '☐'}</span> GARANTÍA</span>
              <span class="diag-crm"><b>REPORTE CRM:</b><span class="diag-crm-line"></span></span>
            </div>
          </div>

          <!-- Niveles posteriores -->
          <div class="niveles-header">Revisión de los niveles de aditivos y lubricantes del tractor LOVOL (Posterior al mantenimiento o reparación)</div>

          <div class="niveles-grid">
            ${this.renderNivelBar('aceite_frenos', 'NIVEL DE ACEITE DE FRENOS', getNivel(nivelesPosteriores, 'aceite_frenos'), na('aceite_frenos'))}
            ${this.renderNivelBar('aceite_eje_delantero', 'NIVEL DE ACEITE DE EJE DELANTERO', getNivel(nivelesPosteriores, 'aceite_eje_delantero'), na('aceite_eje_delantero'))}
            ${this.renderNivelBar('aceite_sistema_direccion', 'NIVEL DE ACEITE DE SISTEMA DE DIRECCIÓN', getNivel(nivelesPosteriores, 'aceite_sistema_direccion'), na('aceite_sistema_direccion'))}
            ${this.renderNivelBar('aceite_motor', 'NIVEL DE ACEITE DE MOTOR', getNivel(nivelesPosteriores, 'aceite_motor'), na('aceite_motor'))}
            ${this.renderNivelBar('combustible', 'NIVEL DE COMBUSTIBLE', getNivel(nivelesPosteriores, 'combustible'), na('combustible'))}
            ${this.renderNivelBar('anticongelante', 'NIVEL DE ANTICONGELANTE', getNivel(nivelesPosteriores, 'anticongelante'), na('anticongelante'))}
            ${this.renderNivelBar('aceite_transmision', 'NIVEL DE ACEITE DE TRANSMISIÓN', getNivel(nivelesPosteriores, 'aceite_transmision'), na('aceite_transmision'))}
            ${this.renderNivelBar('aceite_sistema_hidraulico', 'NIVEL DE ACEITE DE SISTEMA HIDRÁULICO', getNivel(nivelesPosteriores, 'aceite_sistema_hidraulico'), na('aceite_sistema_hidraulico'))}
            ${this.renderNivelBar('aceite_maza_izquierda', 'NIVEL DE ACEITE DE MAZA IZQUIERDA', getNivel(nivelesPosteriores, 'aceite_maza_izquierda'), na('aceite_maza_izquierda'))}
            ${this.renderNivelBar('aceite_maza_derecha', 'NIVEL DE ACEITE MAZA DERECHA', getNivel(nivelesPosteriores, 'aceite_maza_derecha'), na('aceite_maza_derecha'))}
          </div>

          <!-- Firmas página 2 -->
          <div style="display:flex; justify-content:space-between; margin-top:0; gap:5mm;">
            <div style="flex:1; border:none; outline:none; background:none;">
              ${o.usuario_firma_url ? `<img src="${o.usuario_firma_url}" style="width:100%; max-height:15mm; object-fit:contain; display:block; margin:0 auto 1mm;" onerror="this.style.display='none'">` : ''}
              ${o.nombre_usuario ? `<div style="font-size:7pt; text-align:center; margin-bottom:1mm;">${o.nombre_usuario}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:1mm; text-align:center;"><span style="font-weight:bold; color:#2352A4;">USUARIO</span> &nbsp;<span style="color:#000; font-style:italic;">Nombre y firma</span></div>
            </div>
            <div style="flex:1; border:none; outline:none; background:none;">
              ${o.tecnico_servicio_firma_url ? `<img src="${o.tecnico_servicio_firma_url}" style="width:100%; max-height:15mm; object-fit:contain; display:block; margin:0 auto 1mm;" onerror="this.style.display='none'">` : ''}
              ${o.tecnico_servicio ? `<div style="font-size:7pt; text-align:center; margin-bottom:1mm;">${o.tecnico_servicio}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:1mm; text-align:center;"><span style="font-weight:bold; color:#2352A4;">TÉCNICO DE SERVICIO</span> &nbsp;<span style="color:#000; font-style:italic;">Nombre y firma</span></div>
            </div>
            <div style="flex:1; border:none; outline:none; background:none;">
              ${o.responsable_posventa_firma_url ? `<img src="${o.responsable_posventa_firma_url}" style="width:100%; max-height:15mm; object-fit:contain; display:block; margin:0 auto 1mm;" onerror="this.style.display='none'">` : ''}
              ${o.responsable_posventa ? `<div style="font-size:7pt; text-align:center; margin-bottom:1mm;">${o.responsable_posventa}</div>` : ''}
              <div style="border-top:1px solid #000; width:100%;"></div>
              <div style="font-size:6pt; margin-top:1mm; text-align:center;"><span style="font-weight:bold; color:#2352A4;">RESPONSABLE DE ÁREA DE POSVENTA</span> &nbsp;<span style="color:#000; font-style:italic;">Vo. Bo.</span></div>
            </div>
          </div>
          <div style="border-bottom: 3px solid #2352A4; margin-top: 4mm;"></div>

          <div class="page-footer">AMX-PR-FR-01_R12</div>
        </div>
      </div>

      <style>
        /* ==================== ESTILOS GENERALES ==================== */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          font-size: 9pt;
          background: #f0f0f0;
        }

        .print-ot-container {
          max-width: 216mm;
          margin: 0 auto;
        }

        .print-page {
          background: white;
          padding: 10mm 12mm;
          margin-bottom: 10mm;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          min-height: 279mm;
          position: relative;
        }

        /* ==================== HEADER ==================== */
        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5mm;
        }


        .print-header .logo-left,
        .print-header .logo-right {
          height: 12mm;
          max-width: 35mm;
        }

        .print-title {
          font-size: 16pt;
          font-weight: bold;
          color: #1e5070;
          text-transform: uppercase;
          text-align: center;
          flex: 1;
        }

        /* ==================== DATOS PRINCIPALES ==================== */
        .datos-grid {
          margin-bottom: 4mm;
        }

        .dato-row {
          display: flex;
          gap: 4mm;
          margin-bottom: 2mm;
        }

        .dato-field {
          display: flex;
          padding-bottom: 1mm;
        }

        .dato-col-izq {
          flex: 1;
        }

        .dato-col-der {
          flex: 1;
        }

        .dato-label {
          font-weight: normal;
          color: #1e5070;
          margin-right: 1mm;
          white-space: nowrap;
        }

        .dato-label-der {
          min-width: 22mm;
        }

        .dato-value {
          flex: 1;
          font-weight: normal;
          border-bottom: 1px solid #333;
        }

        /* ==================== SECCIONES ==================== */
        .section-title {
          background: none;
          color: #2352A4;
          padding: 2mm 4mm;
          font-weight: bold;
          font-size: 10pt;
          margin: 4mm 0 2mm 0;
          text-align: center;
        }

        .mant-section-title {
          background: none !important;
          color: #4F68B1 !important;
          text-align: left !important;
          margin-top: 1mm !important;
        }

        .diag-section-title {
          background: none !important;
          color: #2352A4 !important;
          text-align: center !important;
          margin-top: -4mm !important;
          margin-bottom: 0 !important;
        }

        .section-subtitle {
          font-size: 8pt;
          margin-bottom: 2mm;
          color: #2352A4;
        }

        /* ==================== NIVELES DE FLUIDOS ==================== */
        .niveles-header {
          background: #2352A4;
          padding: 1.5mm 3mm;
          font-size: 8pt;
          margin-bottom: 3mm;
          color: white;
          font-weight: bold;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .niveles-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2mm;
          margin-bottom: 1mm;
        }

        .nivel-item {
          display: flex;
          flex-direction: column;
        }

        .nivel-label {
          font-size: 5.5pt;
          font-weight: bold;
          margin-bottom: 1mm;
          line-height: 1.1;
          white-space: nowrap;
        }

        .nivel-table {
          border-collapse: collapse;
        }

        .nivel-seg {
          width: 9px;
          height: 9px;
          border: 1px solid #333;
          text-align: center;
          font-size: 9px;
          font-weight: bold;
          padding: 0;
          line-height: 9px;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .nivel-minmax-cell {
          font-size: 5pt;
          color: #666;
          border: none !important;
          padding: 0 !important;
          font-size: 6pt;
          color: #666;
        }

        /* ==================== INSPECCIÓN VISUAL CON MARCAS ==================== */
        .img-caption {
          font-size: 8pt;
          margin: 2mm 0;
          color: #2352A4;
        }

        .ot-visual-layout {
          display: flex;
          gap: 3mm;
          align-items: flex-start;
          margin: 3mm 0;
        }

        .ot-visual-main-imgs {
          flex: 1;
          display: flex;
          gap: 3mm;
          align-items: center;
          justify-content: center;
        }

        .ot-visual-main-imgs img {
          max-height: 55mm;
          object-fit: contain;
        }

        .ot-visual-cabin-box {
          flex: 0 0 auto;
          border: 2px solid #2352A4;
          padding: 2mm;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .ot-visual-cabin-label {
          font-size: 7pt;
          font-weight: bold;
          color: #2352A4;
          margin-bottom: 1mm;
        }

        .ot-visual-cabin-box .ot-img-container {
          margin-bottom: 2mm;
        }

        .ot-visual-cabin-box img {
          max-height: 28mm;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }

        .ot-img-container {
          position: relative;
          display: inline-block;
        }

        .ot-mark {
          position: absolute;
          width: 5%;
          aspect-ratio: 1;
          border: 2px solid #dc3545;
          border-radius: 50%;
          background: rgba(220, 53, 69, 0.25);
          transform: translate(-50%, -50%);
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }

        .img-disclaimer {
          font-size: 7pt;
          font-style: italic;
          color: #2352A4;
          margin-bottom: 3mm;
        }

        /* ==================== OBSERVACIONES ==================== */
        .observaciones-section {
          margin-bottom: 4mm;
        }

        .obs-first-line {
          display: flex;
          align-items: baseline;
          margin-bottom: 5mm;
        }

        .obs-label {
          font-size: 8pt;
          margin-right: 1mm;
          white-space: nowrap;
          color: #2352A4;
        }

        .obs-line {
          flex: 1;
          border-bottom: 1px solid #000;
        }

        .obs-line-full {
          border-bottom: 1px solid #000;
          margin-bottom: 5mm;
        }

        .obs-separator {
          border-bottom: 3px solid #2352A4;
          margin-top: 8mm;
        }

        /* ==================== PROPIEDAD USUARIO ==================== */
        .propiedad-section {
          margin: 4mm 0;
        }

        .propiedad-title {
          background: none;
          color: #2352A4;
          text-align: center;
          padding: 1.5mm;
          font-weight: bold;
          font-size: 9pt;
          border: none;
          margin-top: -3mm;
        }

        .propiedad-content {
          border: 1px solid #1e5070;
          padding: 2mm 3mm;
          font-size: 7pt;
          line-height: 1.4;
        }

        .propiedad-content p {
          margin-bottom: 1mm;
        }

        /* ==================== FIRMAS ==================== */
        .firmas-row {
          display: flex;
          justify-content: space-between;
          margin-top: 8mm;
          gap: 5mm;
        }

        .firma-box {
          flex: 1;
          text-align: center;
          max-width: 45%;
          border: none !important;
          outline: none !important;
          background: none !important;
        }

        .firmas-p2 .firma-box {
          max-width: 32%;
        }

        .firma-line {
          border: none;
          border-top: 1px solid #000;
          width: 100%;
          margin: 0;
          padding: 0;
        }

        .firma-label {
          font-size: 6pt;
          font-weight: bold;
          color: #000;
          margin-top: 1mm;
          text-align: left;
        }

        .firma-sublabel {
          font-size: 6pt;
          color: #000;
          text-align: left;
          font-style: italic;
        }

        /* ==================== TABLAS ==================== */
        .diagnostico-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 7pt;
          margin-bottom: 3mm;
        }

        .diagnostico-table th,
        .diagnostico-table td {
          border: 1px solid #333;
          padding: 1.5mm 2mm;
          text-align: left;
        }

        .diagnostico-table th {
          background: #2352A4;
          color: white;
          font-weight: bold;
          text-align: center;
        }

        .diagnostico-table td.center {
          text-align: center;
        }

        .table-note {
          font-size: 7pt;
          font-style: italic;
        }

        .diag-footer-row {
          display: flex;
          align-items: baseline;
          margin: 2mm 0;
          font-size: 7pt;
          color: #2352A4;
        }

        .diag-footer-row .table-note {
          margin-right: auto;
        }

        .diag-footer-right {
          display: flex;
          align-items: baseline;
          gap: 5mm;
        }

        .diag-footer-right .checkbox {
          font-size: 7pt;
        }

        .diag-crm {
          display: flex;
          align-items: baseline;
          gap: 1mm;
          flex: 1;
          color: #2352A4;
        }

        .diag-crm-line {
          width: 40mm;
          border-bottom: 1px solid #000;
        }

        /* ==================== MANTENIMIENTO COMPLETO (2 COLUMNAS) ==================== */
        .mant-layout-2col {
          display: flex;
          gap: 2mm;
          margin-bottom: 2mm;
        }

        .mant-col-left {
          flex: 1;
          min-width: 0;
        }

        .mant-col-right {
          flex: 1;
          min-width: 0;
        }

        .mant-interval-cell {
          background: #A2D28E;
          color: #000;
          font-weight: bold;
          text-align: center;
          vertical-align: middle;
          padding: 0 !important;
          width: 5mm;
          border: none !important;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .mant-interval-rotated {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          font-size: 6pt;
          white-space: nowrap;
          letter-spacing: 0.5pt;
          display: inline-block;
          padding: 1mm 0;
        }

        .mant-th-interval {
          width: 5mm;
          border: none !important;
          background: transparent !important;
        }

        .mant-num { text-align: center; font-size: 4pt; border: none !important; }
        .mant-th-num { border: none !important; background: transparent !important; }

        .mant-full-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 4pt;
          margin-bottom: 1.5mm;
          table-layout: fixed;
        }

        .mant-full-table th,
        .mant-full-table td {
          border: 0.5px solid #666;
          padding: 0.3mm 0.5mm;
          text-align: center;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .mant-full-table thead th {
          background: #1e5070;
          color: white;
          font-weight: bold;
          font-size: 4.5pt;
          padding: 0.5mm 0.3mm;
        }

        .mant-th-num { width: 5%; }
        .mant-th-desc { width: 30%; text-align: left !important; background: transparent !important; vertical-align: bottom !important; padding: 0 !important; padding-bottom: 2mm !important; border: none !important; }
        .mant-th-acc { width: 12%; background: transparent !important; vertical-align: bottom !important; padding: 0 !important; padding-bottom: 2mm !important; border: none !important; }
        .mant-header-label { background: #1e5070; color: white; padding: 1mm 1mm; font-weight: bold; }
        .mant-th-pts { width: 4%; height: 9mm; padding: 0 !important; vertical-align: bottom; }
        .mant-th-check { width: 4%; }

        .mant-model-header {
          width: 6.4%;
          height: 12mm;
          padding: 1mm 0 !important;
          vertical-align: middle;
          text-align: center;
          background: #fff !important;
          color: #000 !important;
          border: 0.5px solid #999 !important;
          font-weight: normal;
        }

        .mant-model-rotated {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          font-size: 5pt;
          font-weight: 400 !important;
          white-space: normal;
          padding: 0.5mm 0;
          display: inline-block;
          line-height: 1.2;
          text-align: center;
        }

        .mant-th-pts {
          background: #fff !important;
          color: #000 !important;
          border: 0.5px solid #999 !important;
        }

        .mant-model-active {
          background: #dce6f1 !important;
          border: 1.5px solid #ffc107 !important;
          color: #000 !important;
        }

        .mant-num { text-align: center; font-size: 4pt; border: none !important; }
        .mant-desc { text-align: left !important; font-size: 3.5pt; white-space: normal !important; line-height: 1.2; }
        .mant-acc { text-align: center; font-size: 3.5pt; }
        .mant-pts { text-align: center; font-size: 4pt; }
        .mant-check { text-align: center; font-weight: bold; font-size: 5pt; }

        .mant-model-cell {
          text-align: center;
          font-size: 4pt;
          font-weight: bold;
        }

        .mant-cell-symbol {
          background: #cde4f0;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .mant-cell-active {
          background: #98BCDE;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        .mant-cell-realizado {
          background: #7bc47f;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }

        /* Simbología */
        .mant-simbologia {
          padding: 1mm 0;
          font-size: 4pt;
          line-height: 1.4;
          margin-left: 5mm;
        }

        .mant-reporte-crm {
          font-size: 6pt;
          margin-top: 1mm;
          margin-left: 5mm;
          display: flex;
          align-items: baseline;
          gap: 1mm;
        }

        /* ==================== GARANTÍA ==================== */
        .garantia-row {
          display: flex;
          align-items: center;
          gap: 10mm;
          margin: 3mm 0;
          font-size: 9pt;
        }

        .garantia-check {
          display: flex;
          align-items: center;
          gap: 2mm;
        }

        .checkbox {
          font-size: 12pt;
        }

        .reporte-crm {
          display: flex;
          gap: 2mm;
        }

        .crm-value {
          border-bottom: 1px solid #333;
          min-width: 30mm;
        }

        /* ==================== FOOTER ==================== */
        .page-footer {
          position: absolute;
          bottom: 2mm;
          right: 10mm;
          font-size: 7pt;
          color: #2352A4;
        }

        /* ==================== PRINT STYLES ==================== */
        @media print {
          body {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .navbar, nav, .sidebar, aside, #loading, #toast-container, #modal-container, .no-print {
            display: none !important;
          }

          .main-layout, #app, #content, .content {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
          }

          .print-ot-container {
            max-width: 100% !important;
            zoom: 1 !important;
          }

          .print-page {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 8mm !important;
            page-break-after: always;
            min-height: auto;
          }

          .print-page:last-child {
            page-break-after: avoid;
          }

          @page {
            size: 216mm 340mm;
            margin: 5mm;
          }

          /* Mantener colores */
          .section-title,
          .propiedad-title,
          .niveles-header,
          .diagnostico-table th,
          .nivel-seg,
          .mant-full-table thead th,
          .mant-interval-cell,
          .mant-cell-symbol,
          .mant-model-cell,
          .mant-cell-active,
          .mant-cell-realizado,
          .ot-mark {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Layout 2 columnas en impresión */
          .mant-layout-2col {
            display: flex !important;
            gap: 1.5mm;
          }
        }
      </style>
    `;

    return html;
  }
};
